"use client";

import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FileText, ClipboardList, LogOut, User as UserIcon, FilePlus2 } from "lucide-react";
import logo from "../assets/DesarrollandoTerritorios.png";

const ROLE_LABELS_BY_ID = {
  1: "Solicitante",
  2: "Planeación",
  3: "Hacienda",
  4: "Jurídica",
  5: "Alcalde",
  6: "Administrador",
  7: "Presupuesto",
  8: "Almacén",
  9: "Validador (Inexistencia)",
  10: "Secretario (Inexistencia)",
  11: "Alcalde (Inexistencia)",
  12: "Abogado",
  14: "SST",
  15: "Gerencial",
  16: "Plan Acción PDD",
  17: "Seguimiento PDD",
};

const ROLE_LABELS_BY_NAME = {
  solicitante: "Solicitante",
  planeacion: "Planeación",
  hacienda: "Hacienda",
  juridica: "Jurídica",
  alcalde: "Alcalde",
  administrador: "Administrador",
  presupuesto: "Presupuesto",
  almacen: "Almacén",
  validador: "Validador (Inexistencia)",
  firma_secretario: "Secretario (Inexistencia)",
  firma_alcalde: "Alcalde (Inexistencia)",
  abogado: "Abogado",
  sst: "SST",
  gerencial: "Gerencial",
  plan_accion_pdd: "Plan Acción PDD",
  seguimiento_pdd: "Seguimiento PDD",
};

function getRoleId(user) {
  if (!user) return null;
  const raw = user.role_id ?? user.rolId ?? user.roleId ?? null;
  if (raw === null || raw === undefined) return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

function getRoleKeyFromUser(user) {
  if (!user) return null;
  const raw = user.role ?? user.roleName ?? user.rol ?? null;
  if (raw === null || raw === undefined) return null;
  const str = String(raw).trim();
  if (/^\d+$/.test(str)) return null;
  return str.toLowerCase();
}

function getLandingPathByRole(user) {
  const roleId = getRoleId(user);
  const roleKey = getRoleKeyFromUser(user);

  if (roleId !== null) {
    if (roleId === 15) return "/seguimiento-pdd";
    if (roleId === 16) return "/seguimiento-pdd";
    if (roleId === 17) return "/seguimiento-pdd";
    if (roleId === 8) return "/almacen/solicitudes";
    if (roleId === 9 || roleId === 10 || roleId === 11) return "/inexistencia/solicitudes";
    if ([2, 3, 4, 5, 6, 7, 12, 14].includes(roleId)) return "/dashboard";
    return null;
  }

  if (roleKey) {
    if (roleKey === "gerencial") return "/seguimiento-pdd";
    if (roleKey === "plan_accion_pdd") return "/seguimiento-pdd";
    if (roleKey === "seguimiento_pdd") return "/seguimiento-pdd";
    if (roleKey === "almacen") return "/almacen/solicitudes";
    if (
      roleKey === "validador" ||
      roleKey === "firma_secretario" ||
      roleKey === "firma_alcalde"
    ) {
      return "/inexistencia/solicitudes";
    }
    if (
      ["planeacion", "hacienda", "juridica", "alcalde", "administrador", "presupuesto", "abogado", "sst"].includes(roleKey)
    ) {
      return "/dashboard";
    }
  }

  return null;
}

function getNiceRoleLabel(user) {
  if (!user) return "";
  const id = getRoleId(user);
  const key = getRoleKeyFromUser(user);

  if (key) {
    if (ROLE_LABELS_BY_NAME[key]) return ROLE_LABELS_BY_NAME[key];
    return key.charAt(0).toUpperCase() + key.slice(1);
  }

  if (id !== null) {
    if (ROLE_LABELS_BY_ID[id]) return ROLE_LABELS_BY_ID[id];
    return `Rol ${id}`;
  }

  return "";
}

// Roles que pueden ver la tarjeta "Otros CDPs y RPs"
// Solo el solicitante (role_id 1) la ve; el presupuesto llega directo al dashboard.
function canSeeOtrosCDPRP(user) {
  const roleId = getRoleId(user);
  const roleKey = getRoleKeyFromUser(user);
  if (roleId === 1) return true;
  if (roleKey === "solicitante") return true;
  return false;
}

export default function Menu() {
  const navigate = useNavigate();
  const [userEmail, setUserEmail] = useState("");
  const [roleName, setRoleName] = useState("");
  const [showOtrosCDP, setShowOtrosCDP] = useState(false);

  useEffect(() => {
    const load = () => {
      try {
        const storedToken = localStorage.getItem("auth_token");
        const storedUser = localStorage.getItem("auth_user");

        if (!storedToken || !storedUser) {
          navigate("/login", { replace: true });
          return;
        }

        const user = JSON.parse(storedUser);
        setUserEmail(user.email || "");
        setRoleName(getNiceRoleLabel(user));
        setShowOtrosCDP(canSeeOtrosCDPRP(user));

        const landing = getLandingPathByRole(user);
        if (landing) {
          navigate(landing, { replace: true });
          return;
        }
      } catch (e) {
        console.error("Error leyendo sesión:", e);
        navigate("/login", { replace: true });
      }
    };

    load();
  }, [navigate]);

  const handleLogout = async () => {
    try {
      await fetch("https://desarrollandoterritorios.com/api/auth/logout/", {
        method: "POST",
        credentials: "include",
      });
    } catch (e) {
      console.error("Error cerrando sesión:", e);
    } finally {
      localStorage.removeItem("auth_token");
      localStorage.removeItem("auth_user");
      sessionStorage.clear();
      window.location.href = "/login";
    }
  };

  const styles = {
    page: {
      minHeight: "100vh",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      background: "#f7f8fa",
      padding: "24px",
    },
    hero: {
      width: "100%",
      maxWidth: 1100,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 16,
      marginBottom: 24,
    },
    title: { fontSize: 28, fontWeight: 800, color: "#1e3a8a", textAlign: "center" },
    userRow: {
      display: "flex",
      gap: 12,
      alignItems: "center",
      justifyContent: "center",
      flexWrap: "wrap",
      marginTop: 6,
    },
    userChip: {
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      background: "#eef2ff",
      color: "#1e3a8a",
      borderRadius: 999,
      padding: "10px 14px",
      fontSize: 14,
      boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
    },
    logoutBtn: {
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      background: "#ef4444",
      color: "white",
      border: "none",
      borderRadius: 999,
      padding: "10px 16px",
      cursor: "pointer",
      boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
    },
    gridWrap: { width: "100%", display: "flex", justifyContent: "center" },
    grid: {
      width: "100%",
      maxWidth: 1100,
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
      gap: 20,
    },
    card: {
      display: "block",
      textDecoration: "none",
      color: "inherit",
      borderRadius: 16,
      background: "white",
      border: "1px solid #e5e7eb",
      padding: 24,
      boxShadow: "0 6px 18px rgba(0,0,0,0.06)",
      transition: "transform 120ms ease, box-shadow 120ms ease",
    },
    cardHover: {
      transform: "translateY(-2px)",
      boxShadow: "0 12px 24px rgba(0,0,0,0.08)",
    },
    cardHeader: { display: "flex", alignItems: "center", gap: 16 },
    iconWrap: {
      display: "grid",
      placeItems: "center",
      width: 52,
      height: 52,
      borderRadius: 12,
      background: "#f3f4f6",
      flexShrink: 0,
    },
    iconWrapCDP: {
      display: "grid",
      placeItems: "center",
      width: 52,
      height: 52,
      borderRadius: 12,
      background: "#fef9c3",
      flexShrink: 0,
    },
    cardTitle: { fontSize: 20, fontWeight: 700, color: "#111827", margin: 0 },
    cardDesc: { fontSize: 14, color: "#6b7280", margin: "6px 0 0 0" },
  };

  const withHover = (base) => ({
    style: base,
    onMouseEnter: (e) => Object.assign(e.currentTarget.style, styles.cardHover),
    onMouseLeave: (e) => Object.assign(e.currentTarget.style, base),
  });

  return (
    <div style={styles.page}>
      <section style={styles.hero}>
        <img src={logo} alt="Logo" style={{ height: 180, width: "auto" }} />
        <h1 style={styles.title}>Necesidades De Contratación</h1>

        <div style={styles.userRow}>
          <span style={styles.userChip}>
            <UserIcon size={16} />
            {userEmail || "usuario@correo.com"}
            {roleName ? ` (${roleName})` : ""}
          </span>

          <button type="button" onClick={handleLogout} style={styles.logoutBtn}>
            <LogOut size={18} />
            Cerrar sesión
          </button>
        </div>
      </section>

      <div style={styles.gridWrap}>
        <div style={styles.grid}>
          {/* ── Necesidades ── */}
          <Link to="/dashboard" {...withHover(styles.card)} aria-label="Ir a Necesidades">
            <div style={styles.cardHeader}>
              <div style={styles.iconWrap}>
                <FileText size={22} />
              </div>
              <div>
                <h2 style={styles.cardTitle}>Necesidades</h2>
                <p style={styles.cardDesc}>Crear, revisar y gestionar necesidades/contratos.</p>
              </div>
            </div>
          </Link>

          {/* ── Solicitud Adquisiciones ── */}
          <Link
            to="/solicitud-adquisiciones"
            {...withHover(styles.card)}
            aria-label="Ir a Solicitud Adquisiciones"
          >
            <div style={styles.cardHeader}>
              <div style={styles.iconWrap}>
                <ClipboardList size={22} />
              </div>
              <div>
                <h2 style={styles.cardTitle}>Solicitud Adquisiciones</h2>
                <p style={styles.cardDesc}>Creación y gestión de solicitudes de adquisiciones.</p>
              </div>
            </div>
          </Link>

          {/* ── Otros CDPs y RPs (solo solicitante) ── */}
          {showOtrosCDP && (
            <Link
              to="/otras-necesidades-cdp-rp"
              {...withHover(styles.card)}
              aria-label="Ir a Otros CDPs y RPs"
            >
              <div style={styles.cardHeader}>
                <div style={styles.iconWrapCDP}>
                  <FilePlus2 size={22} color="#854d0e" />
                </div>
                <div>
                  <h2 style={styles.cardTitle}>Otros CDPs y RPs</h2>
                  <p style={styles.cardDesc}>
                    Solicitar CDPs y RPs fuera del flujo normal de contratos.
                  </p>
                </div>
              </div>
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}